import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Star, ShoppingCart, Package, Users, Award, 
  Play, ExternalLink, Share2, Copy, Check, Percent, Link, MessageSquare,
  Edit2, Camera, X, Loader2, Image as ImageIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackProductClick } from '../utils/trackClick';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { handleImageError } from '@/utils/imageUtils';
import ResilientImg from '@/components/ResilientImg';
import SEOHead from '@/components/SEOHead';
import { PartnerBadge, isPartnerSeller } from '@/components/PartnerBadge';
import { FoundingCreatorBadge, isFoundingCreator } from '@/components/FoundingCreatorBadge';
import { VerifiedSellerBadge } from '@/components/VerifiedSellerBadge';
import CreatorAvatar from '@/components/CreatorAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function CreatorStore() {
  const { t } = useTranslation();
  const { creatorSlug } = useParams();
  const navigate = useNavigate();
  const [creator, setCreator] = useState(null);
  const [products, setProducts] = useState([]);
  const [affiliatePrograms, setAffiliatePrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [user, setUser] = useState(null);
  const [showAffiliateModal, setShowAffiliateModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [affiliateLink, setAffiliateLink] = useState(null);
  const [joiningAffiliate, setJoiningAffiliate] = useState(false);
  
  // Edit store state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editFormData, setEditFormData] = useState({
    store_name: '',
    bio: '',
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const avatarInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  
  // Check if current user owns this store
  const isOwner = user && creator && user.user_id === creator.user_id;

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    loadStoreData();
  }, [creatorSlug]);

  const loadStoreData = async () => {
    try {
      // Get creator by slug
      const creatorRes = await axios.get(`/creators/store/${creatorSlug}`);
      setCreator(creatorRes.data);

      // Track store pageview for creator analytics
      try {
        const params = new URLSearchParams(window.location.search);
        await axios.post('/track/pageview', {
          page_type: 'store',
          page_id: creatorSlug,
          referrer: document.referrer || '',
          utm_source: params.get('utm_source') || '',
          utm_medium: params.get('utm_medium') || '',
          utm_campaign: params.get('utm_campaign') || '',
        });
      } catch (err) { console.error('Creator store load error:', err); }

      // Get creator's products
      const productsRes = await axios.get(`/creators/${creatorRes.data.creator_id}/products`);
      setProducts(productsRes.data);

      // Get affiliate programs for these products - always use public endpoint for store pages
      const productIds = productsRes.data.map(p => p.product_id);
      if (productIds.length > 0) {
        try {
          // Fetch public affiliate info for this creator's products
          const publicAffiliateRes = await axios.get(`/creators/${creatorRes.data.creator_id}/affiliate-programs`);
          setAffiliatePrograms(publicAffiliateRes.data);
        } catch (err) {
          // No affiliate programs for this creator — non-fatal.
          console.debug('Affiliate programs lookup failed (non-fatal):', err?.message || err);
          setAffiliatePrograms([]);
        }
      }
    } catch (error) {
      console.error('Error loading store:', error);
      toast.error('Store not found');
    } finally {
      setLoading(false);
    }
  };

  const getAffiliateProgram = (productId) => {
    return affiliatePrograms.find(p => p.product_id === productId && p.is_active);
  };

  const handleBecomeAffiliate = async (product) => {
    if (!user) {
      toast.error('Please sign in to become an affiliate');
      return;
    }

    setSelectedProduct(product);
    setAffiliateLink(null);
    setShowAffiliateModal(true);

    // Check if user already has an affiliate link
    const program = getAffiliateProgram(product.product_id);
    if (program?.my_affiliate_link) {
      setAffiliateLink(program.my_affiliate_link);
    }
  };

  const joinAffiliateProgram = async () => {
    if (!selectedProduct || !user) return;

    setJoiningAffiliate(true);
    const token = localStorage.getItem('token');

    try {
      const response = await axios.post(`/affiliate-links/join`, 
        { product_id: selectedProduct.product_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAffiliateLink(response.data);
      toast.success('You are now an affiliate for this product!');
      
      // Refresh affiliate programs
      loadStoreData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join affiliate program');
    } finally {
      setJoiningAffiliate(false);
    }
  };

  const copyAffiliateLink = async () => {
    if (!affiliateLink) return;
    
    const fullLink = `${window.location.origin}/product/${selectedProduct.product_id}?ref=${affiliateLink.link_code}`;
    try {
      await navigator.clipboard.writeText(fullLink);
      toast.success('Affiliate link copied!');
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const copyStoreLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success('Store link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const shareStore = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${creator?.store_name} - AI Products`,
          text: `Check out ${creator?.store_name} on theailedger.ai`,
          url: window.location.href,
        });
      } catch (err) {
        copyStoreLink();
      }
    } else {
      copyStoreLink();
    }
  };

  // Edit store functions
  const openEditModal = () => {
    setEditFormData({
      store_name: creator?.store_name || '',
      bio: creator?.bio || '',
    });
    setAvatarPreview(creator?.avatar || null);
    setBannerPreview(creator?.banner || null);
    setAvatarFile(null);
    setBannerFile(null);
    setShowEditModal(true);
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setAvatarPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBannerFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setBannerPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const saveStoreProfile = async () => {
    setEditLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Upload avatar if changed
      let avatarUrl = creator?.avatar;
      if (avatarFile) {
        const formData = new FormData();
        formData.append('file', avatarFile);
        const uploadRes = await axios.post(`/upload/image`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }
        });
        avatarUrl = uploadRes.data.url;
      }

      // Upload banner if changed
      let bannerUrl = creator?.banner;
      if (bannerFile) {
        const formData = new FormData();
        formData.append('file', bannerFile);
        const uploadRes = await axios.post(`/upload/image`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }
        });
        bannerUrl = uploadRes.data.url;
      }

      // Update creator profile
      await axios.put(`/creators/me/profile`, {
        store_name: editFormData.store_name,
        bio: editFormData.bio,
        avatar: avatarUrl,
        banner: bannerUrl
      }, { headers });

      toast.success('Store profile updated!');
      setShowEditModal(false);
      
      // Reload store data
      loadStoreData();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setEditLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="animate-pulse text-[#00F0FF]">Loading store...</div>
      </div>
    );
  }

  if (!creator) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center">
        <h1 className="text-3xl font-bold mb-4">Store Not Found</h1>
        <p className="text-neutral-400 mb-6">This store does not exist or has been removed.</p>
        <Button onClick={() => navigate('/')} className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black">
          Go to Marketplace
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* SEO Head */}
      {creator && (
        <SEOHead 
          title={`${creator.store_name} - AI Products Store`}
          description={`Shop AI products from ${creator.store_name}. ${products.length} products available.`}
          path={`/store/${creatorSlug}`}
          type="website"
        />
      )}
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-50">
        <div className="container mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-[#00F0FF] hover:text-[#00C2CC]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Marketplace
          </Button>
          <div className="flex items-center gap-3">
            {/* Edit Store Button - Only for owner */}
            {isOwner && (
              <Button
                size="sm"
                onClick={openEditModal}
                className="bg-white hover:bg-neutral-200 text-black"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit Store
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={shareStore}
              className="border-white/10 hover:bg-white/5"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={copyStoreLink}
              className="border-white/10 hover:bg-white/5"
            >
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
          </div>
        </div>
      </header>

      {/* Store Banner */}
      <div className="relative">
        {/* Banner Image */}
        <div 
          className="h-48 md:h-64 bg-gradient-to-r from-[#00F0FF]/20 via-white/20 to-[#00F0FF]/20"
          style={{
            backgroundImage: creator.banner ? `url(${creator.banner})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        
        {/* Store Info Overlay */}
        <div className="container mx-auto px-6 md:px-12">
          <div className="relative -mt-16 md:-mt-20 pb-8">
            <div className="flex flex-col md:flex-row items-start md:items-end gap-6">
              {/* Avatar */}
              <CreatorAvatar
                storeName={creator.store_name}
                avatarUrl={creator.avatar}
                className="w-32 h-32 md:w-40 md:h-40 rounded-2xl shadow-2xl"
              />

              {/* Store Details */}
              <div className="flex-1">
                <h1 className="text-3xl md:text-4xl font-black mb-2 flex items-center gap-3 flex-wrap" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {creator.store_name}
                  {isPartnerSeller(creator) && <PartnerBadge size="md" />}
                  {isFoundingCreator(creator) && (
                    <FoundingCreatorBadge number={creator.founding_creator_number} />
                  )}
                  {creator.stripe_onboarding_complete && (
                    <VerifiedSellerBadge verified={true} size="md" />
                  )}
                </h1>
                <p className="text-neutral-400 mb-4 max-w-2xl">{creator.bio || 'AI product creator'}</p>
                
                {/* Stats */}
                <div className="flex flex-wrap gap-6 mb-4">
                  <div className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-[#00F0FF]" />
                    <span className="font-bold">{products.length}</span>
                    <span className="text-neutral-400">Products</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5 text-white" />
                    <span className="font-bold">{creator.total_sales || 0}</span>
                    <span className="text-neutral-400">Sales</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-[#00F0FF]" />
                    <span className="text-neutral-400">Verified Seller</span>
                  </div>
                </div>

                {/* Message Seller Button */}
                {user ? (
                  user.user_id !== creator.user_id && (
                    <Button
                      onClick={() => navigate(`/messages?to=${creator.user_id}`)}
                      className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-semibold"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Message Seller
                    </Button>
                  )
                ) : (
                  <Button
                    onClick={() => toast.info('Please sign in to message this seller')}
                    variant="outline"
                    className="border-[#00F0FF]/50 text-[#00F0FF] hover:bg-[#00F0FF]/10"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Message Seller
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Products Section */}
      <div className="container mx-auto px-6 md:px-12 py-12">
        <h2 className="text-2xl font-bold mb-8">Products by {creator.store_name}</h2>
        
        {products.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-16 h-16 text-neutral-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">No products yet</h3>
            <p className="text-neutral-400">This creator has not listed any products yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map(product => {
              const affiliateProgram = getAffiliateProgram(product.product_id);
              
              return (
                <div
                  key={product.product_id}
                  className="group bg-neutral-900/50 border border-white/10 rounded-2xl overflow-hidden hover:border-[#00F0FF]/30 transition-all"
                >
                  {/* Product Image */}
                  <div 
                    onClick={() => { trackProductClick(product.product_id, 'creator_store_thumb'); navigate(`/product/${product.product_id}`, {
                      state: { fromStore: true, storeSlug: creatorSlug, storeName: creator.store_name }
                    }); }}
                    className="aspect-video bg-neutral-800 relative overflow-hidden cursor-pointer"
                  >
                    <ResilientImg 
                      src={product.images?.[0]?.startsWith('/api') 
                        ? `${process.env.REACT_APP_BACKEND_URL}${product.images[0]}` 
                        : product.images?.[0] || 'https://via.placeholder.com/400x225'
                      } 
                      alt={product.name} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {/* Badges */}
                    <div className="absolute top-3 right-3 flex flex-col gap-2">
                      {product.video_demo_url && (
                        <div className="bg-[#00F0FF] text-black px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                          <Play className="w-3 h-3" />
                          DEMO
                        </div>
                      )}
                      {affiliateProgram && (
                        <div className="bg-[#00F0FF] text-black px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                          <Percent className="w-3 h-3" />
                          {affiliateProgram.commission_rate}% AFFILIATE
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Product Info */}
                  <div className="p-5">
                    <h3 
                      onClick={() => { trackProductClick(product.product_id, 'creator_store_title'); navigate(`/product/${product.product_id}`, {
                        state: { fromStore: true, storeSlug: creatorSlug, storeName: creator.store_name }
                      }); }}
                      className="font-bold text-lg mb-2 line-clamp-1 group-hover:text-[#00F0FF] transition-colors cursor-pointer"
                    >
                      {product.name}
                    </h3>
                    <p className="text-neutral-400 text-sm mb-4 line-clamp-2">
                      {product.description}
                    </p>

                    {/* Rating */}
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 fill-[#00F0FF] text-[#00F0FF]" />
                        <span className="font-bold text-sm">{product.rating?.toFixed(1) || '5.0'}</span>
                      </div>
                      <span className="text-neutral-500 text-sm">
                        ({product.total_reviews || 0} reviews)
                      </span>
                    </div>

                    {/* Price */}
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl font-black text-[#00F0FF]">
                        ${product.price?.toFixed(2)}
                      </span>
                      <span className="text-sm text-neutral-500">
                        {product.total_sales || 0} sales
                      </span>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => { trackProductClick(product.product_id, 'creator_store_view_btn'); navigate(`/product/${product.product_id}`, {
                          state: { fromStore: true, storeSlug: creatorSlug, storeName: creator.store_name }
                        }); }}
                        className="flex-1 bg-[#00F0FF] hover:bg-[#00C2CC] text-black rounded-full"
                      >
                        View Details
                      </Button>
                      {affiliateProgram && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleBecomeAffiliate(product)}
                          className="border-[#00F0FF]/50 text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded-full"
                        >
                          <Users className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Affiliate Modal */}
      <Dialog open={showAffiliateModal} onOpenChange={setShowAffiliateModal}>
        <DialogContent className="bg-neutral-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Percent className="w-6 h-6 text-[#00F0FF]" />
              Affiliate Program
            </DialogTitle>
          </DialogHeader>
          
          {selectedProduct && (
            <div className="space-y-6 mt-4">
              {/* Product Info */}
              <div className="flex items-center gap-4 p-4 bg-neutral-800 rounded-xl">
                <img 
                  src={selectedProduct.images?.[0]?.startsWith('/api') 
                    ? `${process.env.REACT_APP_BACKEND_URL}${selectedProduct.images[0]}` 
                    : selectedProduct.images?.[0]
                  }
                  alt={selectedProduct.name}
                  className="w-16 h-16 rounded-lg object-cover"
                />
                <div>
                  <h3 className="font-bold">{selectedProduct.name}</h3>
                  <p className="text-[#00F0FF] font-bold">${selectedProduct.price?.toFixed(2)}</p>
                </div>
              </div>

              {/* Commission Info */}
              {getAffiliateProgram(selectedProduct.product_id) && (
                <div className="bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-neutral-400">Commission Rate</span>
                    <span className="text-2xl font-black text-[#00F0FF]">
                      {getAffiliateProgram(selectedProduct.product_id).commission_rate}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-neutral-400">Cookie Duration</span>
                    <span className="font-bold">
                      {getAffiliateProgram(selectedProduct.product_id).cookie_days} days
                    </span>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <span className="text-neutral-400 text-sm">You will earn per sale:</span>
                    <span className="block text-xl font-black text-[#00F0FF]">
                      ${(selectedProduct.price * getAffiliateProgram(selectedProduct.product_id).commission_rate / 100).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              {/* Affiliate Link or Join Button */}
              {affiliateLink ? (
                <div className="space-y-3">
                  <label className="block text-sm font-semibold">Your Affiliate Link:</label>
                  <div className="flex gap-2">
                    <div className="flex-1 bg-neutral-800 border border-white/10 rounded-xl px-4 py-3 text-sm text-neutral-300 truncate">
                      <Link className="w-4 h-4 inline mr-2 text-[#00F0FF]" />
                      {`${window.location.origin}/product/${selectedProduct.product_id}?ref=${affiliateLink.link_code}`}
                    </div>
                    <Button
                      onClick={copyAffiliateLink}
                      className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-neutral-500">
                    Share this link to earn {getAffiliateProgram(selectedProduct.product_id)?.commission_rate}% on every sale!
                  </p>
                  
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-3 mt-4">
                    <div className="bg-neutral-800 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold">{affiliateLink.clicks || 0}</p>
                      <p className="text-xs text-neutral-400">Clicks</p>
                    </div>
                    <div className="bg-neutral-800 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold">{affiliateLink.conversions || 0}</p>
                      <p className="text-xs text-neutral-400">Sales</p>
                    </div>
                    <div className="bg-neutral-800 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-[#00F0FF]">${(affiliateLink.total_earnings || 0).toFixed(2)}</p>
                      <p className="text-xs text-neutral-400">Earned</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {!user ? (
                    <div className="text-center py-4">
                      <p className="text-neutral-400 mb-4">Sign in to become an affiliate</p>
                      <Button
                        onClick={() => {
                          setShowAffiliateModal(false);
                          navigate('/');
                        }}
                        className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black"
                      >
                        Sign In
                      </Button>
                    </div>
                  ) : (
                    <>
                      <p className="text-neutral-400 text-sm">
                        Join this affiliate program to earn commissions by promoting this product. 
                        You will get a unique link to share.
                      </p>
                      <Button
                        onClick={joinAffiliateProgram}
                        disabled={joiningAffiliate}
                        className="w-full bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-bold rounded-full"
                      >
                        {joiningAffiliate ? 'Joining...' : 'Become an Affiliate'}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 mt-12">
        <div className="container mx-auto px-6 md:px-12 text-center text-neutral-500 text-sm">
          <p>This store is powered by <span className="text-[#00F0FF]">theailedger.ai</span></p>
        </div>
      </footer>

      {/* Edit Store Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-neutral-900 border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-white" />
              Edit Your Store
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Banner Upload */}
            <div>
              <label className="block text-sm font-semibold mb-2">Store Banner</label>
              <div 
                className="relative h-32 rounded-xl overflow-hidden bg-gradient-to-r from-[#00F0FF]/20 via-white/20 to-[#00F0FF]/20 cursor-pointer group"
                onClick={() => bannerInputRef.current?.click()}
                style={{
                  backgroundImage: bannerPreview ? `url(${bannerPreview})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="text-center">
                    <Camera className="w-8 h-8 mx-auto mb-2" />
                    <span className="text-sm">Click to upload banner</span>
                  </div>
                </div>
                {!bannerPreview && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-neutral-400">
                      <ImageIcon className="w-8 h-8 mx-auto mb-2" />
                      <span className="text-sm">Click to add banner (1200x300 recommended)</span>
                    </div>
                  </div>
                )}
              </div>
              <input
                ref={bannerInputRef}
                type="file"
                accept="image/*"
                onChange={handleBannerChange}
                className="hidden"
              />
            </div>

            {/* Avatar Upload */}
            <div>
              <label className="block text-sm font-semibold mb-2">Store Avatar</label>
              <div className="flex items-center gap-4">
                <div 
                  className="w-24 h-24 rounded-xl bg-gradient-to-br from-[#00F0FF] to-white p-0.5 cursor-pointer group relative"
                  onClick={() => avatarInputRef.current?.click()}
                >
                  <div className="w-full h-full rounded-xl bg-neutral-800 flex items-center justify-center overflow-hidden">
                    {avatarPreview ? (
                      <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-3xl font-bold text-[#00F0FF]">
                        {editFormData.store_name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    )}
                  </div>
                  <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Camera className="w-6 h-6" />
                  </div>
                </div>
                <div className="text-sm text-neutral-400">
                  <p>Click to upload avatar</p>
                  <p className="text-xs">Recommended: 200x200px</p>
                </div>
              </div>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="hidden"
              />
            </div>

            {/* Store Name */}
            <div>
              <label className="block text-sm font-semibold mb-2">Store Name</label>
              <Input
                value={editFormData.store_name}
                onChange={(e) => setEditFormData({ ...editFormData, store_name: e.target.value })}
                placeholder="Your store name"
                className="bg-neutral-800 border-white/10"
              />
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-semibold mb-2">Bio / Description</label>
              <Textarea
                value={editFormData.bio}
                onChange={(e) => setEditFormData({ ...editFormData, bio: e.target.value })}
                placeholder="Tell customers about your store and what you offer..."
                className="bg-neutral-800 border-white/10 min-h-[100px]"
              />
              <p className="text-xs text-neutral-500 mt-1">
                A good bio helps build trust with customers
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowEditModal(false)}
                className="flex-1 border-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={saveStoreProfile}
                disabled={editLoading || !editFormData.store_name.trim()}
                className="flex-1 bg-white hover:bg-neutral-200 text-black"
              >
                {editLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CreatorStore;
